// Phase 1320: Real-time social proof notification toasts
import { useState, useEffect, useCallback } from "react";
import { MapPin, CheckCircle } from "lucide-react";

const ACTIVITY_TEMPLATES = [
  { city: "Naples", action: "requested a free roof estimate", time: "2 minutes ago" },
  { city: "Fort Myers", action: "scheduled a roof inspection", time: "5 minutes ago" },
  { city: "Sarasota", action: "submitted an insurance claim inquiry", time: "8 minutes ago" },
  { city: "Cape Coral", action: "booked emergency repair service", time: "12 minutes ago" },
  { city: "Bradenton", action: "requested a metal roofing quote", time: "15 minutes ago" },
  { city: "Venice", action: "scheduled a free estimate", time: "18 minutes ago" },
  { city: "Bonita Springs", action: "requested a tile roof consultation", time: "22 minutes ago" },
  { city: "North Port", action: "submitted a roof replacement inquiry", time: "25 minutes ago" },
  { city: "Punta Gorda", action: "booked a roof inspection", time: "28 minutes ago" },
  { city: "Marco Island", action: "requested an emergency assessment", time: "32 minutes ago" },
  { city: "Estero", action: "scheduled a shingle roof quote", time: "35 minutes ago" },
  { city: "Port Charlotte", action: "submitted a storm damage claim", time: "38 minutes ago" },
  { city: "Atlanta", action: "requested a free roof estimate", time: "3 minutes ago" },
  { city: "Savannah", action: "booked a roof inspection", time: "10 minutes ago" },
  { city: "Marietta", action: "requested emergency repair", time: "14 minutes ago" },
];

const NAMES = ["Michael R.", "Jennifer S.", "Robert T.", "Amanda L.", "David W.", "Sarah K.", "James M.", "Lisa P.", "Chris B.", "Maria G."];

interface SocialProofNotificationProps {
  enabled?: boolean;
  delay?: number;
  interval?: number;
}

const SocialProofNotification = ({ enabled = true, delay = 15000, interval = 30000 }: SocialProofNotificationProps) => {
  const [notification, setNotification] = useState<{ name: string; city: string; action: string; time: string } | null>(null);
  const [visible, setVisible] = useState(false);

  const showNotification = useCallback(() => {
    const activity = ACTIVITY_TEMPLATES[Math.floor(Math.random() * ACTIVITY_TEMPLATES.length)];
    const name = NAMES[Math.floor(Math.random() * NAMES.length)];
    setNotification({ name, ...activity });
    setVisible(true);

    setTimeout(() => setVisible(false), 5000);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    
    // Check if user has dismissed social proof
    try {
      if (sessionStorage.getItem("obc_social_proof_off") === "true") return;
    } catch { /* noop */ }

    const initialTimer = setTimeout(showNotification, delay);
    const repeatingTimer = setInterval(showNotification, interval);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(repeatingTimer);
    };
  }, [enabled, delay, interval, showNotification]);

  if (!notification || !visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 z-40 max-w-xs animate-in slide-in-from-left-full duration-500 bg-card border border-border rounded-lg shadow-lg p-4 cursor-pointer"
      onClick={() => setVisible(false)}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
          <CheckCircle className="h-5 w-5 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            {notification.name} from {notification.city}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            just {notification.action}
          </p>
          <div className="flex items-center gap-1 mt-1">
            <MapPin className="h-3 w-3 text-accent" />
            <span className="text-xs text-accent">{notification.time}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialProofNotification;
